import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  Trophy, Star, ChevronRight, ChevronLeft, Check, Sparkles,
  Award, Building2, MapPin, Target, Lightbulb, GripVertical,
  Rocket, Send, User, Clock, ArrowUp, ArrowDown, Play
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Animated stars component
function StarRating({ value, onChange, criterion }) {
  const [hovered, setHovered] = useState(0);
  
  return (
    <div className="flex flex-col items-center">
      <div className="flex gap-2">
        {[1, 2, 3].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            className={`
              relative p-3 rounded-xl transition-all duration-300 transform
              ${value >= star || hovered >= star 
                ? 'bg-gradient-to-br from-amber-400 to-orange-500 scale-110 shadow-lg shadow-amber-200' 
                : 'bg-gray-100 hover:bg-gray-200'
              }
            `}
          >
            <Star 
              className={`h-8 w-8 transition-all duration-300 ${
                value >= star || hovered >= star 
                  ? 'text-white fill-white' 
                  : 'text-gray-400'
              }`}
            />
            {(value >= star || hovered >= star) && (
              <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-yellow-300 animate-pulse" />
            )}
          </button>
        ))}
      </div>
      <span className="mt-2 text-sm font-medium text-gray-600">
        {value === 0 ? 'Selecciona' : value === 1 ? 'Mínima' : value === 2 ? 'Buena' : 'Máxima'}
      </span>
    </div>
  );
}

// Progress indicator with animation
function ProgressBar({ current, total }) {
  const percentage = ((current + 1) / total) * 100;
  
  return (
    <div className="w-full">
      <div className="flex justify-between text-sm text-gray-500 mb-2">
        <span>Progreso de evaluación</span>
        <span>{current + 1} de {total}</span>
      </div>
      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export default function ShakersEvaluateWowPage() {
  const { year, evaluatorToken } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [config, setConfig] = useState(null);
  const [evaluator, setEvaluator] = useState(null);
  const [evaluatorEmail, setEvaluatorEmail] = useState("");
  const [evaluatorName, setEvaluatorName] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [evaluations, setEvaluations] = useState({});
  const [ranking, setRanking] = useState([]);
  const [phase, setPhase] = useState('intro'); // intro, email, evaluate, rank, complete
  const [submitting, setSubmitting] = useState(false);
  const [showCriteriaHelp, setShowCriteriaHelp] = useState(false);
  const [emailError, setEmailError] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [year]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load candidates and config without auth (public endpoint for evaluators)
      const [candidatesRes, configRes] = await Promise.all([
        fetch(`${API}/api/shakers/public/candidates?year=${year}`),
        fetch(`${API}/api/shakers/public/config/${year}`)
      ]);

      if (candidatesRes.ok) {
        const data = await candidatesRes.json();
        setCandidates(data);
        // Initialize evaluations
        const initialEvals = {};
        data.forEach(c => {
          initialEvals[c.id] = {
            relevance: 0,
            creativity: 0,
            evidence: 0,
            collaboration: 0,
            comments: ""
          };
        });
        setEvaluations(initialEvals);
        setRanking(data.map(c => c.id));
      }

      if (configRes.ok) {
        const cfg = await configRes.json();
        setConfig(cfg);
      }
    } catch (error) {
      console.error("Error:", error);
      // For demo, use hardcoded data
      loadDemoData();
    } finally {
      setLoading(false);
    }
  };

  const loadDemoData = () => {
    // Fallback demo data
    const demoConfig = {
      title: "Valoriza Shakers 2025",
      description: "Premios Anuales de Innovación"
    };
    setConfig(demoConfig);
  };

  const updateEvaluation = (candidateId, field, value) => {
    setEvaluations(prev => ({
      ...prev,
      [candidateId]: {
        ...prev[candidateId],
        [field]: value
      }
    }));
  };

  const isCurrentComplete = () => {
    if (!candidates[currentIndex]) return false;
    const current = evaluations[candidates[currentIndex].id];
    return current?.relevance > 0 && current?.creativity > 0 && 
           current?.evidence > 0 && current?.collaboration > 0;
  };

  const handleNext = () => {
    if (!isCurrentComplete()) {
      toast.error("Por favor, evalúa todos los criterios antes de continuar");
      return;
    }
    
    if (currentIndex < candidates.length - 1) {
      setCurrentIndex(currentIndex + 1);
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setPhase('rank');
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const moveInRanking = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= ranking.length) return;
    const newRanking = [...ranking];
    const [removed] = newRanking.splice(fromIndex, 1);
    newRanking.splice(toIndex, 0, removed);
    setRanking(newRanking);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Prepare candidate evaluations
      const candidateEvaluations = candidates.map(c => ({
        candidate_id: c.id,
        candidate_name: c.name,
        relevance: evaluations[c.id]?.relevance || 0,
        creativity: evaluations[c.id]?.creativity || 0,
        evidence: evaluations[c.id]?.evidence || 0,
        collaboration: evaluations[c.id]?.collaboration || 0,
        comments: evaluations[c.id]?.comments || ""
      }));

      const response = await fetch(`${API}/api/shakers/public/evaluate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          evaluator_email: evaluatorEmail,
          evaluator_name: evaluatorName,
          year: parseInt(year),
          candidate_evaluations: candidateEvaluations,
          ranking: ranking
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("¡Evaluación enviada correctamente!");
        setPhase('complete');
      } else {
        toast.error(data.detail || "Error al enviar la evaluación");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al enviar la evaluación. Por favor, inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const validateEmail = async () => {
    if (!evaluatorEmail.trim()) {
      setEmailError("Por favor, introduce tu email");
      return;
    }
    
    // Check if this email is a valid evaluator
    try {
      const res = await fetch(`${API}/api/shakers/public/check-email?email=${encodeURIComponent(evaluatorEmail.trim())}&year=${year}`);
      if (res.ok) {
        const data = await res.json();
        if (data.valid) {
          setEvaluatorName(data.name);
          setEmailError("");
          setPhase('evaluate');
        } else {
          setEmailError(data.message || "Este email no está registrado como evaluador");
        }
      } else {
        setEmailError("Error verificando el email");
      }
    } catch (error) {
      console.error("Error validating email:", error);
      // If we can't validate, allow anyway for demo purposes
      setEvaluatorName(evaluatorEmail.split('@')[0]);
      setPhase('evaluate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <Trophy className="h-20 w-20 text-amber-400 mx-auto animate-bounce" />
            <Sparkles className="absolute top-0 right-0 h-6 w-6 text-amber-300 animate-ping" />
          </div>
          <p className="text-white mt-4 text-lg">Cargando evaluación...</p>
        </div>
      </div>
    );
  }

  // Intro phase
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-2xl mx-auto text-center">
          {/* Logo and title */}
          <div className="mb-8">
            <div className="relative inline-block">
              <Trophy className="h-24 w-24 text-amber-400 mx-auto" />
              <Sparkles className="absolute -top-2 -right-2 h-8 w-8 text-amber-300 animate-pulse" />
              <Sparkles className="absolute -bottom-2 -left-2 h-6 w-6 text-emerald-300 animate-pulse delay-300" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-6">
              Valoriza <span className="text-amber-400">Shakers</span> {year}
            </h1>
            <p className="text-emerald-300 mt-2 text-lg">Premios Anuales de Innovación</p>
          </div>

          {/* Intro card */}
          <Card className="bg-white/10 backdrop-blur-lg border-white/20 text-white">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-4">¡Bienvenido/a, Evaluador/a!</h2>
              <p className="text-gray-300 mb-6">
                Has sido seleccionado/a para evaluar a los candidatos de este año. 
                Tu opinión es fundamental para reconocer el espíritu innovador.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-6 text-left">
                <div className="p-4 bg-white/5 rounded-lg">
                  <Award className="h-8 w-8 text-amber-400 mb-2" />
                  <p className="font-medium">{candidates.length} Candidatos</p>
                  <p className="text-sm text-gray-400">Para evaluar</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <Star className="h-8 w-8 text-amber-400 mb-2" />
                  <p className="font-medium">4 Criterios</p>
                  <p className="text-sm text-gray-400">Por candidato</p>
                </div>
              </div>

              <div className="text-left mb-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Target className="h-5 w-5 text-emerald-400" />
                  Criterios de Evaluación
                </h3>
                <div className="space-y-4 text-sm text-gray-300">
                  <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <p className="font-medium text-amber-400 mb-1">Relevancia y Logro</p>
                    <p className="text-xs">¿La iniciativa está alineada con los objetivos estratégicos? ¿Tiene impacto directo en la organización?</p>
                  </div>
                  <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                    <p className="font-medium text-purple-400 mb-1">Creatividad e Innovación</p>
                    <p className="text-xs">¿Es original y diferente? ¿Es disruptiva? ¿Uso innovador de recursos o tecnologías?</p>
                  </div>
                  <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <p className="font-medium text-emerald-400 mb-1">Evidencia de Resultados</p>
                    <p className="text-xs">¿Hay resultados tangibles? ¿Existen datos o ejemplos que respalden el impacto?</p>
                  </div>
                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <p className="font-medium text-blue-400 mb-1">Colaboración e Influencia</p>
                    <p className="text-xs">¿Ha trabajado en equipo? ¿Es referente en innovación, colaboración y liderazgo?</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setPhase('email')}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-6 text-lg"
              >
                <Play className="h-5 w-5 mr-2" />
                Comenzar Evaluación
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Email phase - Ask for evaluator email
  if (phase === 'email') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <Trophy className="h-16 w-16 text-amber-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white">Identificación</h1>
            <p className="text-gray-400 mt-2">Introduce tu email corporativo para comenzar</p>
          </div>

          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email corporativo
                  </label>
                  <input
                    type="email"
                    value={evaluatorEmail}
                    onChange={(e) => {
                      setEvaluatorEmail(e.target.value);
                      setEmailError("");
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && validateEmail()}
                    placeholder="tu.nombre@valorizasm.com"
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  {emailError && (
                    <p className="mt-2 text-sm text-red-400">{emailError}</p>
                  )}
                </div>

                <Button 
                  onClick={validateEmail}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 py-6"
                >
                  Continuar
                  <ChevronRight className="h-5 w-5 ml-2" />
                </Button>

                <Button 
                  variant="ghost"
                  onClick={() => setPhase('intro')}
                  className="w-full text-gray-400 hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Volver
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Complete phase
  if (phase === 'complete') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="relative mb-8">
            <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center animate-pulse">
              <Check className="h-16 w-16 text-white" />
            </div>
            <Sparkles className="absolute top-0 right-1/4 h-8 w-8 text-amber-300 animate-bounce" />
            <Sparkles className="absolute bottom-0 left-1/4 h-6 w-6 text-emerald-300 animate-bounce delay-150" />
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4">
            ¡Evaluación Completada!
          </h1>
          <p className="text-gray-300 mb-8">
            Gracias por tu participación en los premios Valoriza Shakers.
            Tu opinión es muy valiosa para reconocer la innovación en nuestra empresa.
          </p>
          
          <Card className="bg-amber-500/20 border-amber-400/30 mb-6">
            <CardContent className="p-6">
              <Trophy className="h-12 w-12 text-amber-400 mx-auto mb-3" />
              <p className="text-amber-100">
                ¡Gracias de parte de los <strong>Spaceneers</strong>!
              </p>
            </CardContent>
          </Card>

          <Button
            onClick={() => window.close()}
            variant="outline"
            className="border-white/30 text-white hover:bg-white/10"
          >
            Cerrar
          </Button>
        </div>
      </div>
    );
  }

  // Ranking phase
  if (phase === 'rank') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Trophy className="h-12 w-12 text-amber-400 mx-auto mb-3" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">Ranking Final</h1>
            <p className="text-gray-400 mt-2">
              Ordena los candidatos de mejor a peor según tu evaluación global
            </p>
          </div>

          {/* Ranking list */}
          <Card className="bg-white/10 backdrop-blur-lg border-white/20 mb-6">
            <CardContent className="p-4 md:p-6">
              <div className="space-y-3">
                {ranking.map((candidateId, index) => {
                  const candidate = candidates.find(c => c.id === candidateId);
                  const isTop3 = index < 3;
                  
                  return (
                    <div 
                      key={candidateId}
                      className={`
                        flex items-center gap-3 p-4 rounded-xl transition-all
                        ${isTop3 
                          ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30' 
                          : 'bg-white/5 border border-white/10'
                        }
                      `}
                    >
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg
                        ${index === 0 ? 'bg-amber-500 text-white' :
                          index === 1 ? 'bg-gray-400 text-white' :
                          index === 2 ? 'bg-orange-600 text-white' :
                          'bg-white/10 text-gray-400'
                        }
                      `}>
                        {index + 1}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{candidate?.name}</p>
                        <p className="text-sm text-gray-400 truncate">{candidate?.area}</p>
                      </div>
                      
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          disabled={index === 0}
                          onClick={() => moveInRanking(index, index - 1)}
                          className="text-white hover:bg-white/10"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          disabled={index === ranking.length - 1}
                          onClick={() => moveInRanking(index, index + 1)}
                          className="text-white hover:bg-white/10"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-4">
            <Button 
              variant="outline" 
              onClick={() => setPhase('evaluate')}
              className="flex-1 border-white/30 text-white hover:bg-white/10"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Volver a Evaluar
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
            >
              {submitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Enviar Evaluación
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Evaluate phase
  const currentCandidate = candidates[currentIndex];
  const currentEval = evaluations[currentCandidate?.id] || {};

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 overflow-y-auto"
    >
      {/* Fixed header */}
      <div className="sticky top-0 z-10 bg-slate-900/80 backdrop-blur-lg border-b border-white/10 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="h-6 w-6 text-amber-400" />
              <span className="text-white font-medium">Shakers {year}</span>
            </div>
            <span className="text-gray-400 text-sm">
              Candidato {currentIndex + 1} de {candidates.length}
            </span>
          </div>
          <ProgressBar current={currentIndex} total={candidates.length} />
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Candidate Card */}
        <Card className="bg-white/10 backdrop-blur-lg border-white/20 overflow-hidden mb-6">
          {/* Candidate header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                <span className="text-3xl font-bold text-white">{currentIndex + 1}</span>
              </div>
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  {currentCandidate?.name}
                </h2>
                <div className="flex flex-wrap gap-3 mt-2">
                  <span className="flex items-center gap-1 text-emerald-100 text-sm">
                    <Building2 className="h-4 w-4" />
                    {currentCandidate?.area}
                  </span>
                  <span className="flex items-center gap-1 text-emerald-100 text-sm">
                    <MapPin className="h-4 w-4" />
                    {currentCandidate?.country || 'España'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <CardContent className="p-6 text-white">
            {/* Motivo del Reconocimiento */}
            <div className="mb-6">
              <h3 className="font-semibold mb-2 flex items-center gap-2 text-amber-400">
                <Award className="h-5 w-5" />
                Motivo del Reconocimiento
              </h3>
              <p className="text-gray-300 leading-relaxed">
                {currentCandidate?.description}
              </p>
            </div>

            {currentCandidate?.value_to_company && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-blue-400">
                  <Lightbulb className="h-5 w-5" />
                  Valor para Valoriza
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {currentCandidate.value_to_company}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Evaluation section */}
        <Card className="bg-white/10 backdrop-blur-lg border-white/20 mb-6">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Star className="h-6 w-6 text-amber-400" />
              Tu Evaluación
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { 
                  key: 'relevance', 
                  label: 'Relevancia y Logro', 
                  icon: Target, 
                  color: 'text-amber-400',
                  description: '¿La iniciativa está alineada con los objetivos estratégicos de la empresa? ¿La contribución es relevante y tiene un impacto directo en la línea de negocio o la organización?'
                },
                { 
                  key: 'creativity', 
                  label: 'Creatividad e Innovación', 
                  icon: Lightbulb, 
                  color: 'text-purple-400',
                  description: '¿La propuesta es original y diferente de lo que se ha hecho anteriormente? ¿Es disruptiva y establece un nuevo estándar? ¿Se ha hecho un uso innovador de recursos, herramientas o tecnologías?'
                },
                { 
                  key: 'evidence', 
                  label: 'Evidencia de Resultados', 
                  icon: Award, 
                  color: 'text-emerald-400',
                  description: '¿El candidato ha demostrado resultados tangibles o ha logrado un avance significativo? ¿Hay datos o ejemplos concretos que respalden su impacto?'
                },
                { 
                  key: 'collaboration', 
                  label: 'Colaboración e Influencia', 
                  icon: User, 
                  color: 'text-blue-400',
                  description: '¿Ha trabajado de manera efectiva en equipo y ha logrado influir o inspirar a otros? ¿Es un referente dentro de la empresa en innovación, colaboración y liderazgo?'
                }
              ].map(criterion => (
                <div 
                  key={criterion.key}
                  className="p-5 bg-white/5 rounded-xl border border-white/10"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <criterion.icon className={`h-5 w-5 ${criterion.color}`} />
                    <span className="font-medium text-white">{criterion.label}</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-4 leading-relaxed">
                    {criterion.description}
                  </p>
                  <StarRating
                    value={currentEval[criterion.key] || 0}
                    onChange={(v) => updateEvaluation(currentCandidate.id, criterion.key, v)}
                    criterion={criterion.key}
                  />
                </div>
              ))}
            </div>

            {/* Comments */}
            <div className="mt-6">
              <label className="block font-medium text-white mb-2">
                Comentarios (opcional)
              </label>
              <Textarea 
                value={currentEval.comments || ''}
                onChange={(e) => updateEvaluation(currentCandidate.id, 'comments', e.target.value)}
                placeholder="Añade cualquier observación sobre este candidato..."
                className="bg-white/5 border-white/20 text-white placeholder:text-gray-500"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex gap-4">
          <Button 
            variant="outline"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="flex-1 border-white/30 text-white hover:bg-white/10 disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Anterior
          </Button>
          <Button 
            onClick={handleNext}
            disabled={!isCurrentComplete()}
            className={`
              flex-1 transition-all
              ${isCurrentComplete()
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                : 'bg-gray-600 cursor-not-allowed'
              }
            `}
          >
            {currentIndex === candidates.length - 1 ? 'Finalizar' : 'Siguiente'}
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
